import { inject, injectable } from "tsyringe";
import log from "loglevel";
import type { INavigateTo } from "@/services/NavigateTo";
import { TOKEN_INavigateTo } from "@/services/NavigateTo";
import { type INavigationState, TOKEN_INavigationState } from "./NavigationState";
import { TOKEN_IWorldBuilder, type IWorldBuilder } from "./WorldBuilder";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "./ProjectWorldReader";
import { TOKEN_IRealtime, type IRealtime } from "./Realtime";
import { TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";
import { TOKEN_IProjectEventGenerator, type IProjectEventGenerator } from "./ProjectEventGenerator";
import { neverEmptyString } from "@/lib/utils";
import { generateShortClientIdentity } from "@utilitydelta/nosignup-js";

export const TOKEN_INavigationRedirect = Symbol('INavigationRedirect');

const PROJECT_GRID_SIZE_DEFAULT = 64;

export interface INavigationRedirect {
    /**
     * Checks current navigation state and redirects if needed. If on root route, redirects to most recent project or creates a new one
     * @returns project id to load
     */
    checkAndRedirect(force: boolean): Promise<string | undefined>;
    createNewProject(gridSize: number): Promise<string>;
    forkProject(projectId: string, addHistoryNavigate: boolean): Promise<string | undefined>;
}

@injectable()
export default class NavigationRedirect implements INavigationRedirect {
    constructor(
        @inject(TOKEN_INavigationState) private navigation: INavigationState,
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IWorldBuilder) private worldBuilder: IWorldBuilder,
        @inject(TOKEN_INavigateTo) private navigateTo: INavigateTo,
        @inject(TOKEN_IRealtime) private realtime: IRealtime,
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus,
        @inject(TOKEN_IProjectEventGenerator) private projectEventGenerator: IProjectEventGenerator,
    ) { }

    public async checkAndRedirect(force: boolean): Promise<string | undefined> {
        const navigationState = this.navigation.state;

        await this.realtime.disconnectAll();

        if (navigationState.isFork && navigationState.projectId !== undefined) {
            return await this.forkProject(navigationState.projectId, false);
        }

        if (!force && navigationState.projectId) {

            const shareKeyHash = neverEmptyString(navigationState.shareKey) ? await generateShortClientIdentity(navigationState.shareKey!) : undefined;
            const result = await this.worldBuilder.mustLoadFromServerIfNotLocal(navigationState.projectId, shareKeyHash, navigationState.symmetricKey);
            this.projectBus.projectLoaded(result.projectWorld.projectMetadata.id);

            this.projectEventGenerator.userVisit(result.projectWorld.projectMetadata.id);
            return result.projectWorld.projectMetadata.id;
        }

        // Try to get the most recent project
        const mostRecentProjectId = await this.projectWorldReader.getMostRecentProjectId();

        if (mostRecentProjectId) {
            await this.worldBuilder.mustLoadFromServerIfNotLocal(mostRecentProjectId, undefined, undefined);
            this.navigateTo.SilentNavigateToProjectId(mostRecentProjectId);
            this.projectBus.projectLoaded(mostRecentProjectId);

            log.info(`Redirecting to most recent project: ${mostRecentProjectId}`);
            this.projectEventGenerator.userVisit(mostRecentProjectId);

            return mostRecentProjectId;
        }

        // No existing projects, create a new one (force or no project to load)
        log.info("No existing projects found, creating a new project");
        const newProjectId = await this.worldBuilder.createLocalProject(PROJECT_GRID_SIZE_DEFAULT);

        log.info(`Redirecting to newly created project: ${newProjectId}`);
        this.navigateTo.SilentNavigateToProjectId(newProjectId);
        this.projectBus.projectLoaded(newProjectId);

        this.projectEventGenerator.userVisit(newProjectId);

        return newProjectId;
    }

    public forkProject = async (projectId: string, addHistoryNavigate: boolean): Promise<string | undefined> => {
        const navigationState = this.navigation.state;

        const shareKeyHash = neverEmptyString(navigationState.shareKey) ? await generateShortClientIdentity(navigationState.shareKey!) : undefined;
        await this.worldBuilder.mustLoadFromServerIfNotLocal(navigationState.projectId!, shareKeyHash, navigationState.symmetricKey);

        const newProjectId = await this.worldBuilder.forkProject(projectId);

        log.info(`Redirecting to newly created project: ${newProjectId}`);
        if (addHistoryNavigate) {
            this.navigateTo.NavigateToProjectId(newProjectId);
        } else {
            this.navigateTo.SilentNavigateToProjectId(newProjectId);
        }
        await this.projectEventGenerator.userVisit(newProjectId);
        this.projectBus.projectLoaded(newProjectId);

        return newProjectId;
    }

    public createNewProject = async (gridSize: number) => {
        await this.realtime.disconnectAll();

        const newProjectId = await this.worldBuilder.createLocalProject(gridSize);
        this.navigateTo.NavigateToProjectId(newProjectId);
        log.info(`New project created with ID: ${newProjectId}`);

        await this.projectEventGenerator.userVisit(newProjectId);
        this.projectBus.projectLoaded(newProjectId);

        return newProjectId;
    }
}